import axios from "axios"
import { z } from "zod"
import _ from "lodash"

import { extendedConventionUserSchema } from "@/models/convention_user"

export const conventionSchema = z.object({
  id: z.string(),
  name: z.string(),
  convention_type: z.enum(["SINGLE_USER", "ORGANIZATION"]),
  description: z.string().optional().nullable().default(""),
  status: z.enum(["pending", "active", "expired", "deactivated", "stalled"]),
  created_at: z.string().datetime({ offset: true }),
  activate_at: z.coerce.date().nullable().optional(),
  expire_at: z.coerce.date().nullable().optional(),
  deactivate_at: z.coerce.date().nullable().optional(),
  max_users: z.number().int().default(1).nullable().optional()
})

export type Convention = z.infer<typeof conventionSchema>

export const extendedConventionSchema = conventionSchema.extend({
  users: z.lazy(() => extendedConventionUserSchema.array().default([])).optional()
})

export type ExtendedConvention = z.infer<typeof extendedConventionSchema>

export const editableConventionSchema = z.object({
  name: z.string({ required_error: "Campo obbligatorio." }).trim().min(1, { message: "Campo obbligatorio." }),
  convention_type: z.enum(["SINGLE_USER", "ORGANIZATION"]),
  max_users: z
    .number({ invalid_type_error: "Utenti massimi deve essere un numero" })
    .int()
    .default(1)
    .nullable()
    .optional(),
  description: z.string().optional().nullable(),
  activate_at: z.coerce.date().nullable().optional(),
  expire_at: z.coerce.date().nullable().optional(),
  deactivate_at: z.coerce.date().nullable().optional(),
  user_name: z.string({ required_error: "Campo obbligatorio." }).trim().min(1, { message: "Campo obbligatorio." }),
  user_surname: z.string({ required_error: "Campo obbligatorio." }).trim().min(1, { message: "Campo obbligatorio." }),
  user_telephone: z.string().nullable().optional()
})

const refineExpireDates = (data: EditableConvention) => {
  const { activate_at, expire_at } = data
  return !(activate_at && expire_at && expire_at < activate_at)
}

const refineDeactivateDates = (data: EditableConvention) => {
  const { activate_at, deactivate_at } = data
  return !(activate_at && deactivate_at && deactivate_at < activate_at)
}

export type EditableConvention = z.infer<typeof editableConventionSchema>

export const createConventionSchema = editableConventionSchema
  .extend({
    username: z
      .string({ required_error: "Campo obbligatorio." })
      .email({ message: "Inserisci un indirizzo email valido." })
      .refine(
        async (username) => {
          if (z.string().email().safeParse(username).success) {
            const isTaken = await debouncedCheckUsernames(username)
            return !isTaken
          }
        },
        {
          message: "Questa email è già presente a sistema."
        }
      )
  })
  .refine(refineExpireDates, {
    path: ["expire_at"],
    message: "La scadenza non può precedere l'attivazione."
  })
  .refine(refineDeactivateDates, {
    path: ["deactivate_at"],
    message: "La disattivazione non può precedere l'attivazione."
  })

export type CreateConvention = z.infer<typeof createConventionSchema>
export const updateConventionSchema = editableConventionSchema
  .extend({ id: z.string(), username: z.string().email() })
  .refine(refineExpireDates, {
    path: ["expire_at"],
    message: "La scadenza non può precedere l'attivazione."
  })
  .refine(refineDeactivateDates, {
    path: ["deactivate_at"],
    message: "La disattivazione non può precedere l'attivazione."
  })

export type UpdateConvention = z.infer<typeof updateConventionSchema>

export const getConventions = async ({ signal }: { signal: AbortSignal }): Promise<Convention[]> => {
  return axios
    .get("/api/conventions", { signal })
    .then((response) => {
      return response.data.map((convention: any) => conventionSchema.parse(convention))
    })
    .catch((error) => {
      if (!axios.isCancel(error)) console.error(error)
      throw error
    })
}

export const getConvention = async ({
  id,
  signal
}: {
  id: string | null | undefined
  signal: AbortSignal
}): Promise<ExtendedConvention> => {
  // if (!id) throw new Error("Convention ID is required")

  return axios
    .get(`/api/conventions`, { signal })
    .then((response) => {
      return extendedConventionSchema.parse(response.data)
    })
    .catch((error) => {
      if (!axios.isCancel(error)) console.error(error)
      throw error
    })
}

export const createConvention = async (convention: z.infer<typeof createConventionSchema>): Promise<Convention> => {
  return axios
    .post("/api/conventions", convention)
    .then((response) => {
      return conventionSchema.parse(response.data)
    })
    .catch((error) => {
      if (!axios.isCancel(error)) console.error(error)
      throw error
    })
}

export const updateConvention = async (convention: z.infer<typeof updateConventionSchema>): Promise<Convention> => {
  return axios
    .patch(`/api/conventions/${convention.id}`, convention)
    .then((response) => {
      return conventionSchema.parse(response.data)
    })
    .catch((error) => {
      if (!axios.isCancel(error)) console.error(error)
      throw error
    })
}

export const destroyConvention = async (id: number): Promise<Convention> => {
  return axios.delete(`/api/conventions/${id}`)
}

export const activateConvention = async (id: number): Promise<Convention> => {
  return axios.patch(`/api/conventions/${id}/activate`)
}

export const deactivateConvention = async (id: number): Promise<Convention> => {
  return axios.patch(`/api/conventions/${id}/deactivate`)
}

export const checkUsernames = async (username: string): Promise<boolean> => {
  return axios
    .get("/api/conventions/check_usernames", { params: { username: username } })
    .then((response) => {
      return response.data
    })
    .catch((error) => {
      if (!axios.isCancel(error)) console.error(error)
      throw error
    })
}

const debouncedCheckUsernames = _.debounce(checkUsernames, 300)
